import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  Injector,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { EventqScrollArea } from '../../primitives/scroll-area/scroll-area';
import type {
  EventqActionableListAppearance,
  EventqActionableListActivateEvent,
  EventqActionableListActivateOption,
  EventqActionableListInfoEvent,
  EventqActionableListItem,
  EventqActionableListStatus,
} from './actionable-list.types';

export type {
  EventqActionableListAppearance,
  EventqActionableListActivateEvent,
  EventqActionableListActivateOption,
  EventqActionableListInfoEvent,
  EventqActionableListItem,
  EventqActionableListStatus,
} from './actionable-list.types';

const DEFAULT_SKELETON_COUNT = 5;
const VIEWPORT_PAD = 8;
const ANCHOR_GAP = 4;

type OverlayKind = 'activate' | 'info';

interface OverlayState {
  readonly kind: OverlayKind;
  readonly itemId: string;
  readonly anchor: HTMLElement;
  readonly lines: readonly string[];
  /** Resolved Activate menu options for this overlay (item override or list-level). */
  readonly activateOptions?: readonly EventqActionableListActivateOption[];
}

interface OverlayStyle {
  readonly top: string;
  readonly left: string;
}

@Component({
  selector: 'eventq-actionable-list',
  imports: [MatIcon, EventqScrollArea],
  templateUrl: './actionable-list.html',
  styleUrl: './actionable-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'eventq-actionable-list-host',
    '[class.eventq-actionable-list-host--popover]': "appearance() === 'popover'",
    '[class.eventq-actionable-list-host--tiles]': "appearance() === 'tiles'",
    '[class.eventq-actionable-list-host--disabled]': 'isInteractionDisabled()',
    '[attr.aria-busy]': "loading() ? 'true' : 'false'",
    '[attr.disabled]': 'disabled() ? "" : null',
  },
})
export class EventqActionableList {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly appearance = input<EventqActionableListAppearance>('popover');
  readonly items = input<readonly EventqActionableListItem[]>([]);
  readonly searchQuery = input('');
  readonly searchPlaceholder = input('Search');
  readonly showSearch = input(true);
  readonly activateLabel = input('Activate');
  readonly loading = input(false);
  /** When true, Activate/search/info are non-interactive and the list greys out. */
  readonly disabled = input(false);
  /**
   * Message options for the Activate dropdown/dropup.
   * `null` = legacy direct activate (no menu). Non-null = menu mode (per-item
   * `activateOptions` override; empty resolved list disables Activate for that row).
   */
  readonly activateOptions = input<readonly EventqActionableListActivateOption[] | null>(null);
  readonly skeletonCount = input<number | null>(null);
  readonly emptyMessage = input('No results');
  readonly listAriaLabel = input('Actionable items');
  /** Max height of the row viewport; the list shrinks to its rows below this cap. */
  readonly listHeight = input<string | null>(null);

  readonly searchQueryChange = output<string>();
  readonly activate = output<EventqActionableListActivateEvent>();
  readonly info = output<EventqActionableListInfoEvent>();

  readonly overlay = signal<OverlayState | null>(null);
  readonly overlayStyle = signal<OverlayStyle | null>(null);

  private readonly overlayPanel = viewChild<ElementRef<HTMLElement>>('overlayPanel');

  /** Disabled greying applies only when not in the loading/skeleton state. */
  readonly isInteractionDisabled = computed(() => this.disabled() && !this.loading());

  readonly usesActivateMenu = computed(() => this.activateOptions() !== null);

  readonly overlayActivateOptions = computed(
    () => this.overlay()?.activateOptions ?? [],
  );

  /** Per-item Activate disabled state; depends on `items` + list-level options for zoneless CD. */
  readonly activateDisabledById = computed(() => {
    const menu = this.usesActivateMenu();
    const listOptions = this.activateOptions();
    const interactionDisabled = this.isInteractionDisabled();
    const record: Record<string, boolean> = {};
    for (const item of this.items()) {
      if (interactionDisabled) {
        record[item.id] = true;
        continue;
      }
      if (!menu) {
        record[item.id] = false;
        continue;
      }
      const options =
        item.activateOptions !== undefined ? item.activateOptions : (listOptions ?? []);
      record[item.id] = options.length === 0;
    }
    return record;
  });

  readonly skeletonIndexes = computed(() => {
    const explicit = this.skeletonCount();
    const count =
      explicit != null && explicit > 0 ? explicit : DEFAULT_SKELETON_COUNT;
    return Array.from({ length: count }, (_, index) => index);
  });

  readonly scrollStyle = computed(() => {
    const height = this.listHeight();
    return height ? { maxHeight: height } : {};
  });

  readonly resolvedShowSearch = computed(() => this.showSearch());

  constructor() {
    const onScroll = (event: Event): void => this.onAncestorScroll(event);
    document.addEventListener('scroll', onScroll, true);

    effect(() => {
      const state = this.overlay();
      if (!state) {
        return;
      }
      afterNextRender(
        () => {
          const panel = this.overlayPanel()?.nativeElement;
          if (!panel) {
            return;
          }
          this.portalOverlay(panel);
          this.positionOverlay(panel, state.anchor, state.kind);
        },
        { injector: this.injector },
      );
    });

    this.destroyRef.onDestroy(() => {
      document.removeEventListener('scroll', onScroll, true);
      this.closeOverlay();
    });
  }

  itemLabelLines(item: EventqActionableListItem): readonly string[] {
    if (item.labelLines && item.labelLines.length > 0) {
      return item.labelLines;
    }
    if (item.label) {
      return [item.label];
    }
    return [];
  }

  statusTone(status: EventqActionableListStatus | undefined): string {
    if (status === 'activated') {
      return 'activated';
    }
    if (status === 'failed') {
      return 'failed';
    }
    return 'idle';
  }

  showStatus(item: EventqActionableListItem): boolean {
    return (
      !!item.statusLabel &&
      item.status != null &&
      item.status !== 'idle'
    );
  }

  /** Item override wins; otherwise list-level options (menu mode only). */
  itemActivateOptions(
    item: EventqActionableListItem,
  ): readonly EventqActionableListActivateOption[] {
    if (item.activateOptions !== undefined) {
      return item.activateOptions;
    }
    return this.activateOptions() ?? [];
  }

  isActivateDisabled(item: EventqActionableListItem): boolean {
    return this.activateDisabledById()[item.id] === true;
  }

  onSearchInput(event: Event): void {
    if (this.isInteractionDisabled()) {
      return;
    }
    const value = (event.target as HTMLInputElement).value;
    this.searchQueryChange.emit(value);
  }

  onActivate(item: EventqActionableListItem, event: Event): void {
    event.stopPropagation();
    if (this.loading() || this.isActivateDisabled(item)) {
      return;
    }
    if (!this.usesActivateMenu()) {
      this.activate.emit({ id: item.id });
      return;
    }
    const options = this.itemActivateOptions(item);
    const anchor = event.currentTarget as HTMLElement;
    const current = this.overlay();
    if (current?.kind === 'activate' && current.itemId === item.id) {
      this.closeOverlay();
      return;
    }
    this.overlay.set({
      kind: 'activate',
      itemId: item.id,
      anchor,
      lines: [],
      activateOptions: options,
    });
  }

  onActivateOption(optionId: string, event: Event): void {
    event.stopPropagation();
    const state = this.overlay();
    if (!state || state.kind !== 'activate') {
      return;
    }
    this.activate.emit({ id: state.itemId, optionId });
    this.closeOverlay();
  }

  onInfo(item: EventqActionableListItem, event: Event): void {
    event.stopPropagation();
    if (this.loading() || this.isInteractionDisabled()) {
      return;
    }
    this.info.emit({ id: item.id });
    const current = this.overlay();
    if (current?.kind === 'info' && current.itemId === item.id) {
      this.closeOverlay();
      return;
    }
    this.openInfo(item, event);
  }

  /** Hover and keyboard focus reveal the detail lines; click still toggles them. */
  onInfoEnter(item: EventqActionableListItem, event: Event): void {
    if (this.loading() || this.isInteractionDisabled()) {
      return;
    }
    const current = this.overlay();
    if (current?.kind === 'activate') {
      return;
    }
    this.openInfo(item, event);
  }

  onInfoLeave(item: EventqActionableListItem): void {
    const current = this.overlay();
    if (current?.kind === 'info' && current.itemId === item.id) {
      this.closeOverlay();
    }
  }

  private openInfo(item: EventqActionableListItem, event: Event): void {
    this.overlay.set({
      kind: 'info',
      itemId: item.id,
      anchor: event.currentTarget as HTMLElement,
      lines: item.infoLines ?? [],
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.overlay()) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (
      target?.closest(
        '[data-testid="activate-options-menu"], [data-testid="actionable-list-info-overlay"], .eventq-actionable-list__activate, .eventq-actionable-list__info',
      )
    ) {
      return;
    }
    this.closeOverlay();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.overlay()) {
      this.closeOverlay();
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    const state = this.overlay();
    const panel = this.overlayPanel()?.nativeElement;
    if (!state || !panel) {
      return;
    }
    this.positionOverlay(panel, state.anchor, state.kind);
  }

  /**
   * Nested scrollers (the incident-report protocol stack) do not emit `window:scroll`.
   * Capture-phase scroll sees those. Scrolling the overlay itself does not close it.
   */
  private onAncestorScroll(event: Event): void {
    if (!this.overlay()) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }
    const panel = this.overlayPanel()?.nativeElement;
    if (panel && (target === panel || panel.contains(target))) {
      return;
    }
    const anchor = this.overlay()?.anchor;
    if (!anchor || !target.contains(anchor)) {
      return;
    }
    this.closeOverlay();
  }

  private closeOverlay(): void {
    const panel = this.overlayPanel()?.nativeElement;
    if (panel?.parentElement === document.body) {
      panel.remove();
    }
    this.overlay.set(null);
    this.overlayStyle.set(null);
  }

  private portalOverlay(panel: HTMLElement): void {
    if (panel.parentElement !== document.body) {
      document.body.appendChild(panel);
    }
  }

  private positionOverlay(
    panel: HTMLElement,
    anchor: HTMLElement,
    kind: OverlayKind,
  ): void {
    const rect = anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const width = panelRect.width || panel.offsetWidth || 180;
    const height = panelRect.height || panel.offsetHeight || 120;

    let left = rect.left;
    if (left + width > window.innerWidth - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, rect.right - width);
    }
    left = Math.max(VIEWPORT_PAD, left);

    const spaceBelow = window.innerHeight - rect.bottom - ANCHOR_GAP - VIEWPORT_PAD;
    const spaceAbove = rect.top - ANCHOR_GAP - VIEWPORT_PAD;
    const preferBelow = kind === 'activate' ? spaceBelow >= Math.min(height, 80) : spaceBelow >= height;
    let top: number;
    if (preferBelow || spaceBelow >= spaceAbove) {
      top = rect.bottom + ANCHOR_GAP;
    } else {
      top = Math.max(VIEWPORT_PAD, rect.top - ANCHOR_GAP - height);
    }

    this.overlayStyle.set({
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
    });
  }
}
